import { ComponentType, useEffect, useState, useRef, ReactNode } from "react";

import Select, {
  GroupBase,
  MenuPlacement,
  SingleValueProps,
  StylesConfig,
} from "react-select";
import { IDropDownOption } from "@/types/DropDown";
import { IFolderTreeOptions } from "@/components/files/folderTree";
import styles from "@/components/forms/inputs/dropDown.module.css";
import classNames from "classnames";
import { useConfirmationModal } from "@/context/confirmationModalContext";
import { useTranslation } from "react-i18next";

interface Props {
  options: IDropDownOption[] | IFolderTreeOptions[] | any;
  defaultValue?: any;
  isMulti?: any;
  useReduxDefaultValue?: boolean;

  onChange?: (
    values:
      | IDropDownOption
      | IFolderTreeOptions
      | IDropDownOption[]
      | IFolderTreeOptions[]
      | any,
    label?: any
  ) => void;
  value?: IDropDownOption[] | IFolderTreeOptions[];
  labelStyle?: React.CSSProperties;
  labelClassName?: string | any;
  mandatory?: boolean;
  label?: string | any;
  onBlur?: any;
  isClearable?: boolean;
  isSearchable?: boolean;
  disabled?: boolean;
  name?: string;
  required?: boolean;
  deleteItem?: boolean;
  newTemplateCreated?: boolean;
  editedItem?: any;
  menuPlacement?: MenuPlacement;
  placeholder?: any;
  noOptionsMessage?: ReactNode;
  resetInput?: any;
  resetSignal?: number;
  showConfirmationModalForResetInput?: boolean;
  confirmationModalMessageForResetInput?: string;
  def?: any;
  wrapperStyle?: any;
  wrapperClassName?: string;
  drawerOpen?: boolean;
  customRef?: any;
  showLoadingIndicator?: boolean;
  menuIsOpen?: boolean;
  getOptionLabel?: (option: any) => React.ReactNode;
  getSingleValue?: ComponentType<SingleValueProps<any>>;
  formatOptionLabel?: (option: any, context: any) => React.ReactNode;
  customStyles?: StylesConfig<any, any, GroupBase<any>> | undefined;
  classNamePrefix?: string;
  formatGroupLabel?: ((group: GroupBase<any>) => ReactNode) | undefined | any;
  externalValue?: any;
  isBatchAction?: boolean;
  dataTestId?: string;
}
export default function DropDown(props: Props) {
  const { t } = useTranslation();
  const { showConfirmationModalAsync } = useConfirmationModal();
  const [values, setValues] = useState<
    | IDropDownOption[]
    | IFolderTreeOptions[]
    | IDropDownOption
    | IFolderTreeOptions
    | any
  >([]);
  const previousValueRef = useRef<any>(values);

  useEffect(() => {
    if (props.resetSignal !== undefined) {
      if (props.isMulti) {
        setValues([]);
      } else setValues(null);
    }
  }, [props.resetSignal]);

  const [defaultValuesAreSet, setDefaultValuesAreSet] =
    useState<boolean>(false);

  const findOptionForValue = (value: any) => {
    for (const key in props.options) {
      const option = props.options[key];

      if (Array.isArray(value)) {
        if (
          value.some(
            (val) => JSON.stringify(val) === JSON.stringify(option.value)
          )
        ) {
          return option;
        }
      }
      if (value === option.value) {
        return option;
      }
    }
  };

  useEffect(() => {
    if (
      props.defaultValue === undefined ||
      props.options.length === 0 ||
      defaultValuesAreSet
    )
      return;

    const preSelectedValue: any = [];
    if (props.isMulti && Array.isArray(props.defaultValue)) {
      props.defaultValue.forEach((val) => {
        const option = findOptionForValue(val);

        if (option) {
          preSelectedValue.push(option);
        }
      });
    } else if (!Array.isArray(props.defaultValue)) {
      const option = findOptionForValue(props.defaultValue);
      if (option) {
        preSelectedValue.push(option);
      }
    } else if (
      props.defaultValue === undefined ||
      props.defaultValue === null
    ) {
      setValues(null);
    }

    if (preSelectedValue.length > 0) {
      setValues(preSelectedValue);
      setDefaultValuesAreSet(true);
    }
  }, [props.defaultValue, props.options, defaultValuesAreSet]);

  useEffect(() => {
    if (props.externalValue !== undefined) {
      return;
    }
    if (props.useReduxDefaultValue) {
      return;
    }
    if (props.onChange) {
      if (!props.isMulti && Array.isArray(values)) {
        props.onChange(values[0], props.name);
      } else {
        props.onChange(values, props.name);
      }
    }
  }, [values]);

  const onChangeHandler = async (selectedOption: any, action?: any) => {
    if (props.useReduxDefaultValue) {
      props.onChange?.(selectedOption);
      return;
    }

    if (props.externalValue !== undefined) {
      if (action?.action === "clear") {
        if (props.showConfirmationModalForResetInput) {
          const isConfirmed = await showConfirmationModalAsync(
            props.confirmationModalMessageForResetInput ||
              t("confirm_clear_field")
          );
          if (!isConfirmed) {
            return;
          }
        }

        if (props.resetInput) {
          props.resetInput();
        }
      }

      props.onChange?.(selectedOption, props.name);
      return;
    }

    if (action?.action === "clear") {
      const currentValue = values;

      if (props.showConfirmationModalForResetInput) {
        const isConfirmed = await showConfirmationModalAsync(
          props.confirmationModalMessageForResetInput ||
            t("confirm_clear_field")
        );
        if (!isConfirmed) {
          setValues(currentValue);
          return;
        }
      }

      previousValueRef.current = selectedOption;
      setValues(selectedOption);
      if (props.resetInput) {
        props.resetInput();
      }
      return;
    }

    previousValueRef.current = values;
    setValues(selectedOption);
  };

  useEffect(() => {
    if (props.editedItem === true) {
      setDefaultValuesAreSet(false);
      setValues([]);
    }
  }, [props.editedItem]);

  useEffect(() => {
    if (props.deleteItem === true) {
      setValues([]);
    }
  }, [props.deleteItem]);
  return (
    <div
      data-testid={props.dataTestId}
      style={{
        ...props.wrapperStyle,
      }}
      className={`${styles.wrapper} ${props.wrapperClassName}`}
    >
      <label
        className={classNames({ [props.labelClassName]: props.labelClassName })}
        style={props.labelStyle}
      >
        {props.mandatory === true ? (
          <>
            {props.label}
            <span style={{ color: "red" }}>*</span>
          </>
        ) : (
          <>{props.label}</>
        )}
      </label>
      <Select
        classNamePrefix={props.classNamePrefix}
        ref={props.customRef}
        id={props.name}
        aria-label="DropDown-Select"
        options={props.options}
        value={
          props.externalValue !== undefined
            ? props.externalValue
            : props.useReduxDefaultValue
            ? props.defaultValue
            : values
        }
        onChange={(selectedOption, action) =>
          onChangeHandler(selectedOption, action)
        }
        defaultValue={props.useReduxDefaultValue && props.defaultValue}
        onBlur={props.onBlur}
        name={props.name}
        isMulti={props.isMulti}
        menuIsOpen={props.menuIsOpen}
        closeMenuOnSelect={!props.isMulti}
        isDisabled={props.disabled}
        isSearchable={props.isSearchable}
        isClearable={props.isClearable}
        required={props.required}
        isLoading={props.showLoadingIndicator ?? props.options.length === 0}
        menuPlacement={props.menuPlacement}
        placeholder={props.placeholder ?? t("select_placeholder")}
        loadingMessage={() => t("loading")}
        noOptionsMessage={({ inputValue }) =>
          props.noOptionsMessage ??
          (inputValue.trim().length > 0
            ? t("no_options_found")
            : t("type_to_search_placeholder"))
        }
        menuPortalTarget={document.body}
        formatOptionLabel={props.formatOptionLabel}
        formatGroupLabel={props.formatGroupLabel}
        getOptionLabel={(option) => option.label}
        // components={
        //   props.getSingleValue
        //     ? { SingleValue: props.getSingleValue }
        //     : undefined
        // }
        components={
          props.getSingleValue ? { SingleValue: props.getSingleValue } : {}
        }
        styles={{
          clearIndicator: (base) => ({
            ...base,
            color: props.isBatchAction ? "#66bab1" : "#ba78de",
            backgroundColor: "#f4f2f2",
            borderRadius: "0.75rem",
            padding: "4px",
            marginRight: "0.3rem",
            "&:hover": {
              color: props.isBatchAction ? "#008c7d" : "#8c1ec8",
            },
          }),
          dropdownIndicator: (base) => ({
            ...base,
            color: props.isBatchAction ? "#66bab1" : "#ba78de",
            backgroundColor: "#f4f2f2",
            borderRadius: "0.75rem",
            padding: "4px",
            margin: "0 0.3rem",
            "&:hover": {
              color: props.isBatchAction ? "#66bab1" : "#8c1ec8",
            },
          }),

          loadingIndicator: (base) => ({
            ...base,
            color: props.isBatchAction ? "#66bab1" : "#ba78de",
          }),

          control: (baseStyles) => ({
            ...baseStyles,
            borderRadius: "0.75rem",
          }),

          valueContainer: (base) => ({
            ...base,
            padding: "0.75rem",
            borderRadius: "0.75rem",
          }),
          input: (base) => ({
            ...base,
            margin: "0",
            padding: "0",
          }),
          option: (styles, { isDisabled, isFocused, isSelected }) => {
            return {
              ...styles,
              borderRadius: "0.75rem",
              backgroundColor: isDisabled
                ? undefined
                : isSelected
                ? "#f4f2f2"
                : isFocused
                ? props.isBatchAction
                  ? "#008c7d"
                  : "#a34bd3"
                : undefined,
              color: isDisabled
                ? "#00000042"
                : isSelected
                ? "black"
                : isFocused
                ? "white"
                : undefined,

              cursor: isDisabled ? "not-allowed" : "default",
            };
          },

          menuPortal: (base) => ({
            ...base,
            borderRadius: "0.75rem",
            zIndex: props.drawerOpen ? 99999 : 9999,
          }),
          menu: (baseStyles) => ({
            ...baseStyles,
            borderRadius: "0.75rem",
          }),
          multiValue: (baseStyles) => ({
            ...baseStyles,
            backgroundColor: props.isBatchAction ? "#008c7d" : "#8c1ec8",
            borderRadius: "0.75rem",
            color: "white",
            padding: "0.25rem 0.5rem 0.25rem 0.25rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "0.25rem",
          }),
          multiValueLabel: (baseStyles) => ({
            ...baseStyles,
            color: "white",
          }),
          multiValueRemove: (baseStyles) => ({
            ...baseStyles,
            borderRadius: "50%",
            color: props.isBatchAction ? "#66bab1" : "#ba78de",
            backgroundColor: "white",
            width: "1.25rem",
            height: "1.25rem",
            padding: "0.025rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            ":hover": {
              color: "#ff1744",
            },
          }),
          ...props.customStyles,
        }}
      />
    </div>
  );
}
